import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Users, Copy, Trash2, Building2, X } from "lucide-react";
import api from "@/lib/api";
import DashboardLayout from "@/components/DashboardLayout";

const PLANS = [
  { key: "starter", label: "Starter — 1 seat" },
  { key: "team", label: "Team — 5 seats" },
  { key: "enterprise", label: "Enterprise — 25 seats" },
];

export default function ClientAccounts() {
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ company_name: "", domain: "", plan: "team", brand_color: "#D4AF37" });

  const load = () => api.get("/client-accounts").then((r) => setAccounts(r.data));
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/client-accounts", form);
      toast.success("Client account created");
      setShowForm(false);
      setForm({ company_name: "", domain: "", plan: "agency_pro", brand_color: "#D4AF37" });
      load();
    } catch { toast.error("Could not create account"); }
  };

  const del = async (id) => { if (!window.confirm("Delete this client account?")) return; await api.delete(`/client-accounts/${id}`); toast.success("Deleted"); load(); };

  const copyReg = (acc) => { navigator.clipboard.writeText(`${window.location.origin}/join/${acc.reg_token}`); toast.success("Self-registration link copied"); };

  return (
    <DashboardLayout title="Client Accounts"
      actions={<button onClick={() => setShowForm(true)} className="btn-gold px-4 py-2.5 rounded-full text-sm flex items-center gap-2" data-testid="create-account-btn"><Plus size={16} /> New Client Account</button>}>
      {accounts.length === 0 ? (
        <div className="iw-card p-12 text-center">
          <Building2 className="text-[#D4AF37] mx-auto mb-3" size={32} />
          <h3 className="font-display text-2xl font-bold">No client accounts</h3>
          <p className="text-[#8B9BB4] mt-2 mb-6">Create a client organization, set its brand and allocate seats.</p>
          <button onClick={() => setShowForm(true)} className="btn-gold px-6 py-3 rounded-full">Create Account</button>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
          {accounts.map((a) => (
            <div key={a.id} className="iw-card p-6" data-testid={`account-${a.id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-display text-xl font-bold">{a.company_name}</h3>
                  {a.domain && <p className="text-xs text-[#8B9BB4]">{a.domain}</p>}
                  <span className="overline mt-2 inline-block">{a.plan.replace("_", " ")}</span>
                </div>
                <span className="h-4 w-4 rounded-full" style={{ background: a.brand_color }} />
              </div>
              <div className="mt-4">
                <div className="flex items-center justify-between text-sm text-[#8B9BB4] mb-1.5">
                  <span className="flex items-center gap-1.5"><Users size={14} /> Seats</span>
                  <span className="text-[#F6F1E4] font-semibold">{a.seats_used} / {a.seat_limit}</span>
                </div>
                <div className="h-1.5 rounded-full bg-[#142B5A] overflow-hidden">
                  <div className="h-full bg-[#D4AF37]" style={{ width: `${Math.min(100, (a.seats_used / a.seat_limit) * 100)}%` }} />
                </div>
              </div>
              <div className="flex gap-2 mt-5">
                <button onClick={() => navigate(`/app/clients/${a.id}`)} className="btn-gold px-3 py-2 rounded-lg text-xs flex items-center gap-1.5 flex-1 justify-center" data-testid={`manage-seats-${a.id}`}><Users size={13} /> Manage Seats</button>
                <button onClick={() => copyReg(a)} className="btn-ghost px-3 py-2 rounded-lg text-xs flex items-center gap-1.5 justify-center" data-testid={`reg-link-${a.id}`} title="Copy self-registration link"><Copy size={13} /></button>
                <button onClick={() => del(a.id)} className="px-3 py-2 rounded-lg text-xs text-red-400 border border-red-400/30 hover:bg-red-400/10 transition-colors"><Trash2 size={13} /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-black/70" onClick={() => setShowForm(false)}>
          <div className="iw-card p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-xl font-bold">New Client Account</h3>
              <button onClick={() => setShowForm(false)}><X size={18} className="text-[#8B9BB4]" /></button>
            </div>
            <form onSubmit={create} className="space-y-4">
              <input className="iw-input" placeholder="Company name" value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} required data-testid="account-name" />
              <input className="iw-input" placeholder="Custom domain (optional) e.g. cards.acme.com" value={form.domain} onChange={(e) => setForm({ ...form, domain: e.target.value })} data-testid="account-domain" />
              <select className="iw-input" value={form.plan} onChange={(e) => setForm({ ...form, plan: e.target.value })} data-testid="account-plan">
                {PLANS.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
              </select>
              <div className="flex items-center gap-3">
                <label className="text-sm text-[#8B9BB4]">Brand color</label>
                <input type="color" value={form.brand_color} onChange={(e) => setForm({ ...form, brand_color: e.target.value })} className="h-9 w-16 rounded bg-transparent cursor-pointer" />
              </div>
              <button className="btn-gold w-full py-3 rounded-full" data-testid="account-submit">Register Client Account</button>
            </form>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
